import { useState, useEffect } from "react";
import { useAuth, apiClient } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Settings, User, Bell, Shield, LogOut, Heart, Cloud, Link2, Unlink, CheckCircle2, Loader2, AlertTriangle, Trash2, Sparkles, Download, Bot, KeyRound, EyeOff, Eye } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { scheduleScrollLockRelease } from "@/lib/scrollLock";

const AI_PROVIDERS = [
  { key: "openai", label: "OpenAI", models: ["gpt-4o", "gpt-4o-mini", "gpt-4-turbo"], help: "Get a key at https://platform.openai.com/api-keys" },
  { key: "anthropic", label: "Anthropic", models: ["claude-sonnet-4-5", "claude-haiku-4-5", "claude-opus-4-5"], help: "Get a key at https://console.anthropic.com/settings/keys" },
  { key: "gemini", label: "Google Gemini", models: ["gemini-2.5-pro", "gemini-2.5-flash", "gemini-1.5-pro"], help: "Get a key at https://aistudio.google.com/apikey" },
  { key: "openrouter", label: "OpenRouter", models: ["meta-llama/llama-3.3-70b-instruct", "deepseek/deepseek-chat", "anthropic/claude-sonnet-4.5"], help: "Get a key at https://openrouter.ai/keys" }
];

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState({
    appointments: true,
    goals: true,
    community: false
  });
  const [driveStatus, setDriveStatus] = useState({ connected: false, account_email: null, loading: true });
  const [dropboxStatus, setDropboxStatus] = useState({ connected: false, account_email: null, loading: true });
  const [backfilling, setBackfilling] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [disconnectAiOpen, setDisconnectAiOpen] = useState(false);
  const [emailConfirm, setEmailConfirm] = useState("");
  const [downloadedAck, setDownloadedAck] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exportChildren, setExportChildren] = useState([]);
  const [exportingId, setExportingId] = useState(null);
  // BYOK AI config
  const [aiCfg, setAiCfg] = useState({ configured: false, provider: null, model: null, key_last4: null });
  const [aiForm, setAiForm] = useState({ provider: "openai", model: "", api_key: "" });
  const [showAiKey, setShowAiKey] = useState(false);
  const [savingAi, setSavingAi] = useState(false);
  const [savingAiPreference, setSavingAiPreference] = useState(false);

  // Make sure the page is scrollable the moment it lands, even if a dialog/select
  // from the previous page left a stale scroll lock on <body>.
  useEffect(() => scheduleScrollLockRelease(), []);

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const [driveRes, dropboxRes, childrenRes, aiRes] = await Promise.all([
          apiClient.get("/drive/status"),
          apiClient.get("/dropbox/status"),
          apiClient.get("/export/children"),
          apiClient.get("/settings/ai")
        ]);
        setDriveStatus({ connected: driveRes.data.connected, account_email: driveRes.data.account_email, loading: false });
        setDropboxStatus({ connected: dropboxRes.data.connected, account_email: dropboxRes.data.account_email, loading: false });
        setExportChildren(childrenRes.data || []);
        setAiCfg(aiRes.data);
        if (aiRes.data?.configured) {
          setAiForm(f => ({ ...f, provider: aiRes.data.provider, model: aiRes.data.model }));
        }
      } catch {
        setDriveStatus(s => ({ ...s, loading: false }));
        setDropboxStatus(s => ({ ...s, loading: false }));
      }
    };
    fetchStatuses();
  }, []);

  const handleSaveAi = async (e) => {
    e.preventDefault();
    if (!aiForm.model || !aiForm.api_key || aiForm.api_key.length < 10) {
      toast.error("Model and API key are required");
      return;
    }
    setSavingAi(true);
    try {
      const r = await apiClient.put("/settings/ai", aiForm);
      setAiCfg(r.data);
      setAiForm(f => ({ ...f, api_key: "" }));
      toast.success("AI key saved securely");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
    setSavingAi(false);
  };

  const handleDisconnectAi = async () => {
    try {
      await apiClient.delete("/settings/ai");
      setAiCfg({ configured: false, provider: null, model: null, key_last4: null });
      setAiForm({ provider: "openai", model: "", api_key: "" });
      toast.success("AI key removed");
    } catch { toast.error("Failed"); }
    setDisconnectAiOpen(false);
  };

  const handleAiPreference = async (ai_disabled) => {
    setSavingAiPreference(true);
    try {
      await apiClient.put("/settings/ai/preference", { ai_disabled });
      setAiCfg((cfg) => ({ ...cfg, ai_disabled }));
      toast.success(ai_disabled ? "AI features are now hidden" : "AI features are now available");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update AI preference");
    }
    setSavingAiPreference(false);
  };

  const handleExportChild = async (child) => {
    setExportingId(child.id);
    try {
      const res = await apiClient.get(`/export/child/${child.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${child.name.replace(/[^a-zA-Z0-9_-]+/g, "_")}_thrively_export.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${child.name}'s data export`);
    } catch (err) {
      toast.error("Export failed");
    }
    setExportingId(null);
  };

  const handleConnectDrive = async () => {
    try {
      const res = await apiClient.get("/oauth/drive/authorize");
      window.location.href = res.data.auth_url;
    } catch {
      toast.error("Failed to start Google Drive connection");
    }
  };

  const handleDisconnectDrive = async () => {
    try {
      await apiClient.delete("/drive/disconnect");
      setDriveStatus({ connected: false, account_email: null, loading: false });
      toast.success("Google Drive disconnected");
    } catch {
      toast.error("Failed to disconnect");
    }
  };

  const handleConnectDropbox = async () => {
    try {
      const res = await apiClient.get("/oauth/dropbox/authorize");
      window.location.href = res.data.auth_url;
    } catch {
      toast.error("Failed to start Dropbox connection");
    }
  };

  const handleDisconnectDropbox = async () => {
    try {
      await apiClient.delete("/dropbox/disconnect");
      setDropboxStatus({ connected: false, account_email: null, loading: false });
      toast.success("Dropbox disconnected");
    } catch {
      toast.error("Failed to disconnect");
    }
  };

  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const res = await apiClient.post("/sync/backfill");
      const { total_documents, drive, dropbox, sample_error } = res.data;
      if (total_documents === 0) {
        toast.success("All documents are already synced");
      } else {
        const parts = [];
        if (drive) parts.push(`Drive: ${drive.synced} synced, ${drive.failed} failed`);
        if (dropbox) parts.push(`Dropbox: ${dropbox.synced} synced, ${dropbox.failed} failed`);
        const anyFail = (drive && drive.failed > 0) || (dropbox && dropbox.failed > 0);
        if (anyFail && sample_error) {
          toast.error(`Sync failed — ${parts.join(" · ")}. Reason: ${sample_error.substring(0, 200)}`, { duration: 12000 });
        } else {
          toast.success(`Backfill complete — ${parts.join(" · ")}`);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill failed");
    }
    setBackfilling(false);
  };

  const handleLogout = () => {
    logout();
    window.location.href = "/";
  };

  const handleDeleteAccount = async () => {
    if (emailConfirm !== user?.email) {
      toast.error("Email does not match");
      return;
    }
    if (!downloadedAck) {
      toast.error("Please confirm you've downloaded your data");
      return;
    }
    setDeleting(true);
    try {
      await apiClient.delete("/auth/account");
      toast.success("Your account and all data have been permanently deleted");
      logout();
      window.location.href = "/";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Account deletion failed. Please contact support.");
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3">
            <Settings className="h-8 w-8" />
            Settings
          </h1>
          <p className="text-muted-foreground mt-1">Manage your account and preferences</p>
        </div>
        <div className="text-right" data-testid="app-version">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Thrively</p>
          <p className="text-sm font-mono font-bold">v0.5</p>
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700">Beta</span>
        </div>
      </div>

      {/* Profile Section */}
      <Card className="border-0 shadow-lg rounded-3xl">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            Profile
          </CardTitle>
          <CardDescription>Your account information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center">
              <span className="font-heading font-bold text-2xl text-primary">
                {user?.name?.charAt(0).toUpperCase() || "U"}
              </span>
            </div>
            <div>
              <h3 className="font-heading font-bold text-lg">{user?.name}</h3>
              <p className="text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          
          <Separator />
          
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input 
                id="name" 
                value={user?.name || ""} 
                disabled 
                className="rounded-xl bg-muted"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email" 
                value={user?.email || ""} 
                disabled 
                className="rounded-xl bg-muted"
              />
            </div>
          </div>
          
          <p className="text-sm text-muted-foreground">
            Profile editing will be available in a future update.
          </p>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card className="border-0 shadow-lg rounded-3xl">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Bell className="h-5 w-5 text-golden-500" />
            Notifications
          </CardTitle>
          <CardDescription>Control your notification preferences</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Appointment Reminders</p>
              <p className="text-sm text-muted-foreground">Get notified before sessions</p>
            </div>
            <Switch 
              checked={notifications.appointments}
              onCheckedChange={(v) => setNotifications({ ...notifications, appointments: v })}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Goal Updates</p>
              <p className="text-sm text-muted-foreground">Milestone achievements and progress</p>
            </div>
            <Switch 
              checked={notifications.goals}
              onCheckedChange={(v) => setNotifications({ ...notifications, goals: v })}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Community Activity</p>
              <p className="text-sm text-muted-foreground">Replies and new discussions</p>
            </div>
            <Switch 
              checked={notifications.community}
              onCheckedChange={(v) => setNotifications({ ...notifications, community: v })}
            />
          </div>
          
          <Button 
            onClick={() => toast.success("Preferences saved!")}
            className="rounded-full w-full mt-4"
          >
            Save Preferences
          </Button>
        </CardContent>
      </Card>

      {/* Cloud Sync Integrations */}
      <Card className="border-0 shadow-lg rounded-3xl relative overflow-hidden" data-testid="cloud-sync-section">
        {user?.subscription_tier !== 'premium' && (
          <div className="absolute inset-0 z-10 bg-white/90 flex flex-col items-center justify-center text-center p-8 rounded-3xl">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <h3 className="font-heading font-bold text-lg mb-1">Premium Feature</h3>
            <p className="text-sm text-muted-foreground max-w-sm mb-4">
              Google Drive & Dropbox auto-sync is part of Family Plus — launching soon.
            </p>
            <Link to="/#pricing" className="text-sm text-primary font-medium hover:underline">See pricing</Link>
          </div>
        )}
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Cloud className="h-5 w-5 text-info" />
            Cloud Sync
            <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary">Premium</span>
          </CardTitle>
          <CardDescription>
            Automatically back up every document you upload to a <span className="font-mono text-xs">Thrively Backup</span> folder in your connected accounts.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Google Drive */}
          <div className="flex items-center justify-between p-4 rounded-2xl border border-border" data-testid="drive-row">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-xl bg-white shadow-sm flex items-center justify-center border flex-shrink-0">
                <svg viewBox="0 0 24 24" className="h-6 w-6">
                  <path d="M4.433 22.396l4-6.93H24l-4 6.93z" fill="#4285F4"/>
                  <path d="M15.567 1.604L7.784 15.396H0L7.784 1.604z" fill="#0F9D58"/>
                  <path d="M24 15.466H8.434L12.434 8.536H24z" fill="#FFCD40"/>
                </svg>
              </div>
              <div className="min-w-0">
                <p className="font-medium flex items-center gap-2">
                  Google Drive
                  {driveStatus.connected && <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />}
                </p>
                <p className="text-xs text-muted-foreground truncate" data-testid="drive-account-email">
                  {driveStatus.loading
                    ? "Checking..."
                    : driveStatus.connected
                      ? (driveStatus.account_email ? `Syncing to ${driveStatus.account_email}` : "Sync enabled")
                      : "Not synced"}
                </p>
              </div>
            </div>
            {driveStatus.loading ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : driveStatus.connected ? (
              <Button variant="outline" size="sm" onClick={handleDisconnectDrive} className="rounded-full" data-testid="disconnect-drive-btn">
                <Unlink className="h-4 w-4 mr-1" /> Stop Sync
              </Button>
            ) : (
              <Button size="sm" onClick={handleConnectDrive} className="rounded-full" data-testid="connect-drive-btn">
                <Link2 className="h-4 w-4 mr-1" /> Enable Sync
              </Button>
            )}
          </div>

          {/* Dropbox */}
          <div className="flex items-center justify-between p-4 rounded-2xl border border-border" data-testid="dropbox-row">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-xl bg-white shadow-sm flex items-center justify-center border flex-shrink-0">
                <svg viewBox="0 0 24 24" className="h-6 w-6">
                  <path d="M6 2l6 3.75L6 9.5 0 5.75z" fill="#0061FF"/>
                  <path d="M18 2l6 3.75-6 3.75-6-3.75z" fill="#0061FF"/>
                  <path d="M0 13.25L6 9.5l6 3.75L6 17z" fill="#0061FF"/>
                  <path d="M18 9.5l6 3.75-6 3.75-6-3.75z" fill="#0061FF"/>
                  <path d="M6 18.25l6-3.75 6 3.75-6 3.75z" fill="#0061FF"/>
                </svg>
              </div>
              <div className="min-w-0">
                <p className="font-medium flex items-center gap-2">
                  Dropbox
                  {dropboxStatus.connected && <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />}
                </p>
                <p className="text-xs text-muted-foreground truncate" data-testid="dropbox-account-email">
                  {dropboxStatus.loading
                    ? "Checking..."
                    : dropboxStatus.connected
                      ? (dropboxStatus.account_email ? `Syncing to ${dropboxStatus.account_email}` : "Sync enabled")
                      : "Not synced"}
                </p>
              </div>
            </div>
            {dropboxStatus.loading ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : dropboxStatus.connected ? (
              <Button variant="outline" size="sm" onClick={handleDisconnectDropbox} className="rounded-full" data-testid="disconnect-dropbox-btn">
                <Unlink className="h-4 w-4 mr-1" /> Stop Sync
              </Button>
            ) : (
              <Button size="sm" onClick={handleConnectDropbox} className="rounded-full" data-testid="connect-dropbox-btn">
                <Link2 className="h-4 w-4 mr-1" /> Enable Sync
              </Button>
            )}
          </div>

          <p className="text-xs text-muted-foreground mt-2">
            New uploads and deletions are mirrored automatically. Use the button below to back up documents you uploaded before sync was enabled (or to retry any that failed).
          </p>

          {(driveStatus.connected || dropboxStatus.connected) && (
            <Button
              variant="outline"
              className="w-full rounded-full"
              onClick={handleBackfill}
              disabled={backfilling}
              data-testid="backfill-sync-btn"
            >
              {backfilling ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Syncing…</>
              ) : (
                <><Cloud className="mr-2 h-4 w-4" /> Sync existing documents now</>
              )}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Privacy */}
      <Card className="border-0 shadow-lg rounded-3xl">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Shield className="h-5 w-5 text-success" />
            Privacy & Security
          </CardTitle>
          <CardDescription>Your data is protected</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-success/10 rounded-2xl">
            <div className="flex items-center gap-3">
              <Shield className="h-8 w-8 text-success" />
              <div>
                <p className="font-heading font-bold">HIPAA-Ready</p>
                <p className="text-sm text-muted-foreground">
                  Your family's data is secured with industry-standard encryption
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>• All data is encrypted in transit and at rest</p>
            <p>• Access limited to authorized family members</p>
            <p>• Regular security audits and updates</p>
          </div>
        </CardContent>
      </Card>

      {/* About */}
      <Card className="border-0 shadow-lg rounded-3xl">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Heart className="h-5 w-5 text-coral-500" />
            About Thrively
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-4">
            <img 
              src="/thrively-logo.png"
              alt="Thrively"
              className="h-12 w-12"
            />
            <div>
              <h3 className="font-heading font-bold">Thrively</h3>
              <p className="text-sm text-muted-foreground">Version 0.5 · Beta</p>
            </div>
          </div>
          <p className="text-muted-foreground text-sm">
            Thrively is designed to empower parents of neurodiverse children 
            by centralizing therapy management, progress tracking, and family support.
            Built with love for families navigating their child's unique journey.
          </p>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-lg rounded-3xl" data-testid="ai-preference-section">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <EyeOff className="h-5 w-5 text-primary" />
            AI preferences
          </CardTitle>
          <CardDescription>Choose whether AI tools are shown anywhere in Thrively.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label htmlFor="disable-ai" className="font-medium">I don't want to use AI</Label>
              <p className="mt-1 text-sm text-muted-foreground">Hide AI summaries and session-note tools. Your saved key is retained if you change your mind.</p>
            </div>
            <Switch
              id="disable-ai"
              checked={Boolean(aiCfg.ai_disabled)}
              onCheckedChange={handleAiPreference}
              disabled={savingAiPreference}
              data-testid="disable-ai-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* AI Settings (BYOK — Premium) */}
      {!aiCfg.ai_disabled && <Card className="border-0 shadow-lg rounded-3xl relative overflow-hidden" data-testid="ai-settings-section">
        {user?.subscription_tier !== 'premium' && (
          <div className="absolute inset-0 z-10 bg-white/90 flex flex-col items-center justify-center text-center p-8 rounded-3xl">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <h3 className="font-heading font-bold text-lg mb-1">Premium Feature</h3>
            <p className="text-sm text-muted-foreground max-w-sm mb-4">
              Bring your own LLM key to enable AI summaries on articles and AI activity extraction from session notes.
            </p>
            <Link to="/#pricing" className="text-sm text-primary font-medium hover:underline">See pricing</Link>
          </div>
        )}
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" />
            AI (Bring Your Own Key)
            <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary">Premium</span>
          </CardTitle>
          <CardDescription>
            Your key is encrypted at rest. Used only when you click an AI button. Nothing is sent to our servers from these calls — the request goes straight from our backend to the provider.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {aiCfg.configured ? (
            <div className="flex items-center justify-between p-3 rounded-2xl border border-success/30 bg-success/5">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-success/10 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-success" />
                </div>
                <div>
                  <p className="font-medium text-sm" data-testid="ai-configured-label">
                    {AI_PROVIDERS.find(p => p.key === aiCfg.provider)?.label || aiCfg.provider}
                    {" · "}
                    <span className="font-mono">{aiCfg.model}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">Key: ••••{aiCfg.key_last4}</p>
                </div>
              </div>
              <AlertDialog open={disconnectAiOpen} onOpenChange={setDisconnectAiOpen}>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="rounded-full" data-testid="disconnect-ai-btn">
                    <Unlink className="h-4 w-4 mr-1" /> Remove
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-3xl" data-testid="disconnect-ai-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-heading">Remove your AI key?</AlertDialogTitle>
                    <AlertDialogDescription>
                      AI features will stop working until you add a new key.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter className="gap-2">
                    <AlertDialogCancel className="rounded-full" data-testid="cancel-disconnect-ai-btn">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={(e) => { e.preventDefault(); handleDisconnectAi(); }}
                      className="rounded-full"
                      data-testid="confirm-disconnect-ai-btn"
                    >
                      <Unlink className="mr-2 h-4 w-4" /> Remove Key
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ) : null}

          <form onSubmit={handleSaveAi} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="ai-provider">Provider</Label>
                <Select
                  value={aiForm.provider}
                  onValueChange={(v) => setAiForm(f => ({ ...f, provider: v, model: "" }))}
                >
                  <SelectTrigger className="rounded-xl" data-testid="ai-provider-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AI_PROVIDERS.map(p => (
                      <SelectItem key={p.key} value={p.key} data-testid={`ai-provider-${p.key}`}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ai-model">Model</Label>
                {aiForm.provider === "openrouter" ? (
                  <Input
                    id="ai-model"
                    list="openrouter-models"
                    value={aiForm.model}
                    onChange={(e) => setAiForm(f => ({ ...f, model: e.target.value }))}
                    placeholder="e.g. anthropic/claude-sonnet-4.5"
                    className="rounded-xl"
                    data-testid="ai-model-input"
                  />
                ) : (
                  <Select
                    value={aiForm.model}
                    onValueChange={(v) => setAiForm(f => ({ ...f, model: v }))}
                  >
                    <SelectTrigger className="rounded-xl" data-testid="ai-model-select">
                      <SelectValue placeholder="Select model" />
                    </SelectTrigger>
                    <SelectContent>
                      {(AI_PROVIDERS.find(p => p.key === aiForm.provider)?.models || []).map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <datalist id="openrouter-models">
                  {(AI_PROVIDERS.find(p => p.key === "openrouter")?.models || []).map(m => <option key={m} value={m} />)}
                </datalist>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ai-key" className="flex items-center gap-2">
                <KeyRound className="h-3.5 w-3.5" />
                API Key
              </Label>
              <div className="relative">
                <Input
                  id="ai-key"
                  type={showAiKey ? "text" : "password"}
                  value={aiForm.api_key}
                  onChange={(e) => setAiForm(f => ({ ...f, api_key: e.target.value }))}
                  placeholder={aiCfg.configured ? "Paste to replace existing key" : "Paste your API key"}
                  className="rounded-xl pr-10 font-mono"
                  autoComplete="off"
                  data-testid="ai-key-input"
                />
                <button
                  type="button"
                  onClick={() => setShowAiKey(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showAiKey ? "Hide key" : "Show key"}
                >
                  {showAiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                {AI_PROVIDERS.find(p => p.key === aiForm.provider)?.help}
              </p>
            </div>

            <Button type="submit" disabled={savingAi} className="rounded-full" data-testid="save-ai-key-btn">
              {savingAi ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</> : <><KeyRound className="mr-2 h-4 w-4" /> {aiCfg.configured ? "Update Key" : "Save Key"}</>}
            </Button>
          </form>
        </CardContent>
      </Card>}

      {/* Data Export */}
      <Card className="border-0 shadow-lg rounded-3xl" data-testid="data-export-section">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Download className="h-5 w-5 text-info" />
            Data Export
          </CardTitle>
          <CardDescription>
            Download a ZIP file per child containing therapists, appointments, goals, activities, and session notes as CSV files. Relationships are preserved by foreign-key columns.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {exportChildren.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Add a child profile to enable exports.</p>
          ) : (
            <div className="space-y-2">
              {exportChildren.map(child => (
                <div key={child.id} className="flex items-center justify-between p-3 rounded-2xl border border-border" data-testid={`export-row-${child.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                    <span className="font-medium text-sm">{child.name}</span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleExportChild(child)}
                    disabled={exportingId === child.id}
                    className="rounded-full"
                    data-testid={`export-btn-${child.id}`}
                  >
                    {exportingId === child.id ? (
                      <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Preparing…</>
                    ) : (
                      <><Download className="mr-1.5 h-4 w-4" /> Download ZIP</>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sign Out */}
      <Card className="border-0 shadow-lg rounded-3xl border-destructive/20">
        <CardContent className="py-4">
          <Button 
            variant="destructive" 
            onClick={handleLogout}
            className="w-full rounded-full"
            data-testid="settings-logout-btn"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign Out
          </Button>
        </CardContent>
      </Card>

      {/* Danger Zone — HIPAA Right to Erasure */}
      <Card className="border-2 border-destructive/30 shadow-lg rounded-3xl bg-destructive/5" data-testid="danger-zone-section">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Danger Zone
          </CardTitle>
          <CardDescription className="text-destructive/80">
            Permanently delete your account and all associated data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-2xl bg-background border border-destructive/20 p-4 space-y-2">
            <p className="text-sm font-medium">This action will permanently:</p>
            <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
              <li>Delete your account, profile, and login</li>
              <li>Delete all child profiles, carers, therapists, appointments, session notes, goals, activities</li>
              <li>Delete every uploaded document from our secure storage (Backblaze B2)</li>
              <li>Remove your contact from our email provider (Brevo)</li>
              <li>Revoke our access to your Google Drive and Dropbox</li>
              <li>Wipe all audit logs and community posts you authored</li>
            </ul>
            <p className="text-sm text-destructive font-medium pt-2">
              This cannot be undone. Make sure to download your documents first.
            </p>
          </div>

          <AlertDialog open={deleteOpen} onOpenChange={(o) => { setDeleteOpen(o); if (!o) { setEmailConfirm(""); setDownloadedAck(false); } }}>
            <AlertDialogTrigger asChild>
              <Button
                variant="destructive"
                className="w-full rounded-full"
                data-testid="open-delete-account-btn"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete My Account
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-3xl max-w-lg" data-testid="delete-account-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-heading text-destructive flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Permanently delete your account?
                </AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-3 text-sm">
                    <p>
                      Once deleted, your data cannot be recovered. We recommend you{" "}
                      {/* Close the dialog before navigating so Radix releases its body scroll lock;
                          navigating while the dialog is still open leaves the page unscrollable. */}
                      <Link
                        to="/resources/documents"
                        className="text-primary underline font-medium"
                        onClick={(e) => {
                          e.preventDefault();
                          setDeleteOpen(false);
                          setTimeout(() => navigate("/resources/documents"), 0);
                        }}
                      >
                        download your documents
                      </Link>{" "}
                      before continuing.
                    </p>
                    <div className="flex items-start gap-2 p-3 bg-destructive/5 border border-destructive/20 rounded-xl">
                      <Checkbox
                        id="downloaded-ack"
                        checked={downloadedAck}
                        onCheckedChange={setDownloadedAck}
                        data-testid="downloaded-ack-checkbox"
                      />
                      <Label htmlFor="downloaded-ack" className="text-sm cursor-pointer leading-snug">
                        I understand this is permanent and I have downloaded any data I want to keep.
                      </Label>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email-confirm" className="text-sm">
                        Type your email <span className="font-mono text-foreground">{user?.email}</span> to confirm:
                      </Label>
                      <Input
                        id="email-confirm"
                        type="email"
                        value={emailConfirm}
                        onChange={(e) => setEmailConfirm(e.target.value)}
                        placeholder={user?.email}
                        className="rounded-xl"
                        autoComplete="off"
                        data-testid="delete-email-confirm-input"
                      />
                    </div>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="gap-2">
                <AlertDialogCancel className="rounded-full" data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => { e.preventDefault(); handleDeleteAccount(); }}
                  disabled={deleting || emailConfirm !== user?.email || !downloadedAck}
                  className="rounded-full bg-destructive hover:bg-destructive/90 disabled:opacity-50"
                  data-testid="confirm-delete-account-btn"
                >
                  {deleting ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Deleting...</>
                  ) : (
                    <><Trash2 className="mr-2 h-4 w-4" /> Permanently Delete</>
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
}
